import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UserStatusControl from "@/components/admin/UserStatusControl";
import AdminCancellations from "@/components/admin/AdminCancellations";
import AdminSettings from "@/components/admin/AdminSettings";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Phone, TrendingUp, Sparkles, Download, CheckCircle2,
  ArrowLeft, RefreshCw, Award, Gift, Wallet, Calendar,
  Send, Bell, Loader2, MessageSquare,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, hint, color = "primary-c" }) {
  return (
    <div className="card-c">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-2xl bg-${color}/10 flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 text-${color}`} strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold truncate">{label}</p>
          <p className="font-display text-2xl leading-none mt-1">{value}</p>
        </div>
      </div>
      {hint && <p className="text-xs text-text-secondary mt-2">{hint}</p>}
    </div>
  );
}

function LeadsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // "open" | "all" | "handled"

  const load = async () => {
    setLoading(true);
    const params = filter === "all" ? "" : `?handled=${filter === "handled"}`;
    try {
      const r = await api.get(`/admin/leads${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(tx("ui_admin.k22d7d0"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const toggleHandled = async (id, current) => {
    try {
      await api.patch(`/admin/leads/${id}`, { handled: !current });
      toast.success(current ? tx("ui_admin.kf7dc13") : tx("ui_admin.k3b9af5"));
      load();
    } catch (e) {
      toast.error(tx("ui_admin.kfe7d2c"));
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/leads.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oresto-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(tx("ui_admin.kb323e5"));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="inline-flex items-center gap-1 p-1 bg-surface-muted rounded-full border border-c" data-testid="leads-filter">
          {[
            { k: "open", label: tx("ui_admin.k78f378") },
            { k: "handled", label: tx("ui_admin.k91234a") },
            { k: "all", label: tx("ui_admin.k3dd16e") },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full ${filter === f.k ? "bg-white shadow" : "text-text-secondary"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="leads-refresh" className="p-2 rounded-full hover:bg-surface-muted"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={downloadCsv} data-testid="leads-csv" className="btn-secondary text-xs !py-1.5 !px-3 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> CSV</button>
        </div>
      </div>

      {loading && <p className="text-sm text-text-secondary text-center py-8">{tx("ui_admin.k8f54e8")}</p>}
      {!loading && items.length === 0 && (
        <div className="card-c text-center py-10">
          <Phone className="w-8 h-8 text-text-secondary mx-auto" strokeWidth={1.5} />
          <p className="text-sm text-text-secondary mt-3">{tx("ui_admin.k94db78")}</p>
          <p className="text-xs text-text-secondary mt-1">{tx("ui_admin.k6159d2")}</p>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs text-text-secondary">{total} lead{total > 1 ? "s" : ""} au total</p>
          {items.map((l) => (
            <div key={l.id} className={`card-c ${l.handled ? "opacity-70" : ""}`} data-testid={`lead-${l.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{l.first_name} {l.last_name}</p>
                    {l.canton && <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-2 py-0.5 rounded-full">{l.canton}</span>}
                    {l.handled && <span className="text-[10px] font-bold uppercase text-primary-c bg-primary-c/10 px-2 py-0.5 rounded-full">{tx("ui_admin.k7510e9")}</span>}
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5">{l.restaurant_name || "—"} · {l.covers_per_day || "?"} couverts/j</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <a href={`mailto:${l.email}`} className="text-primary-c hover:underline">✉️ {l.email}</a>
                    <a href={`tel:${l.phone}`} className="text-primary-c hover:underline">📞 {l.phone}</a>
                  </div>
                  {l.preferred_slot && <p className="text-xs text-text-secondary mt-1">🕒 Souhaité : {l.preferred_slot}</p>}
                  {l.message && <p className="text-sm mt-2 p-2 bg-surface-muted rounded-lg text-text-secondary italic">« {l.message} »</p>}
                  <p className="text-[10px] text-text-secondary mt-2">{tx("tpl.received_on", { date: new Date(l.created_at).toLocaleString("fr-CH") })}</p>
                </div>
                <button
                  onClick={() => toggleHandled(l.id, l.handled)}
                  data-testid={`lead-toggle-${l.id}`}
                  className={`shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold ${l.handled ? "bg-surface-muted text-text-secondary hover:bg-c" : "bg-primary-c text-white hover:bg-primary-c/90"}`}
                >
                  {l.handled ? "Rouvrir" : tx("ui_admin.k0b4029")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "ambassadors" | "admins" | "active" | "trial" | "disabled"
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const { user: me } = useAuth();
  const { t } = useTranslation();
  const PAGE = 50;

  useEffect(() => { const id = setTimeout(() => { setQ(search.trim()); setPage(0); }, 300); return () => clearTimeout(id); }, [search]);
  useEffect(() => {
    setLoading(true);
    api.get("/admin/users", { params: { filter, q, limit: PAGE, skip: page * PAGE } }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  }, [filter, reload, q, page]);
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div>
      <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} maxLength={100} placeholder={t("admin.search_ph")} className="input-c text-sm mb-3" data-testid="admin-user-search" />
      <div className="inline-flex items-center gap-1 p-1 bg-surface-muted rounded-full border border-c mb-4 flex-wrap">
        {[
          { k: "all", label: t("admin.f_all") },
          { k: "ambassadors", label: t("admin.f_ambassadors") },
          { k: "admins", label: "Admins" },
          { k: "active", label: t("admin.f_active") },
          { k: "trial", label: t("admin.f_trial") },
          { k: "disabled", label: t("admin_status.filter") },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => { setFilter(f.k); setPage(0); }}
            data-testid={`user-filter-${f.k}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full ${filter === f.k ? "bg-white shadow" : "text-text-secondary"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {loading && <p className="text-sm text-text-secondary text-center py-8">{t("admin.loading")}</p>}
      {!loading && items.length === 0 && <p className="text-sm text-text-secondary text-center py-8" data-testid="admin-users-empty">{t("admin.no_users")}</p>}
      {!loading && items.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-text-secondary">
            <span data-testid="admin-users-total">{t("admin.accounts", { count: total })}</span>
            {pages > 1 && (
              <span className="flex items-center gap-2">
                <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-2 py-1 rounded-lg border border-c disabled:opacity-40" data-testid="admin-users-prev">‹</button>
                <span data-testid="admin-users-page">{page + 1} / {pages}</span>
                <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="px-2 py-1 rounded-lg border border-c disabled:opacity-40" data-testid="admin-users-next">›</button>
              </span>
            )}
          </div>
          {items.map((u) => (
            <div key={u.id} className="card-c" data-testid={`user-${u.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{u.name || u.email}</p>
                    {u.lifetime_access && <span className="text-[9px] font-bold uppercase text-white bg-primary-c px-2 py-0.5 rounded-full">👑 Admin</span>}
                    {u.ambassador && <span className="text-[9px] font-bold uppercase text-secondary-c bg-secondary-c/10 px-2 py-0.5 rounded-full">{tx("r3.ambassador")}</span>}
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${u.subscription_status === "active" ? "bg-green-100 text-green-700" : "bg-surface-muted text-text-secondary"}`}>{u.subscription_status}</span>
                    {u.disabled && <span className="text-[9px] font-bold uppercase text-white bg-red-600 px-2 py-0.5 rounded-full" data-testid={`user-disabled-badge-${u.id}`}>{t("admin_status.disabled_badge")}</span>}
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5">{u.email}</p>
                  <p className="text-[10px] text-text-secondary mt-1">{tx("tpl.created_on", { date: new Date(u.created_at).toLocaleDateString("fr-CH") })}{u.promo_code && ` · Code : ${u.promo_code}`}</p>
                  <UserStatusControl u={u} selfId={me?.id} onChanged={() => setReload((x) => x + 1)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CANTONS_LIST = ["VD","GE","FR","NE","JU","VS","BE","ZH","BS","BL","AG","SO","SG","TI","LU","ZG","SH","TG","GR","UR","SZ","OW","NW","GL","AR","AI"];
const COVERS_BUCKETS = [
  { v: "<30", label: "< 30 couverts/j" },
  { v: "30-60", label: "30-60 couverts/j" },
  { v: "60-120", label: "60-120 couverts/j" },
  { v: ">120", label: "> 120 couverts/j" },
];

function PushTab() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/app/accueil");
  const [target, setTarget] = useState("all");
  const [cantons, setCantons] = useState([]);
  const [covers, setCovers] = useState([]);
  const [scheduledAt, setScheduledAt] = useState(""); // "YYYY-MM-DDTHH:mm" local
  const [preview, setPreview] = useState({ users: null, devices: null, loading: false });
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState(0);
  const [subUsers, setSubUsers] = useState(0);
  const [loading, setLoading] = useState(true);

  const toggle = (arr, val, setter) => {
    setter(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/push/campaigns");
      setItems(r.data.items || []);
      setDevices(r.data.subscribed_devices || 0);
      setSubUsers(r.data.subscribed_users || 0);
    } catch {
      toast.error(tx("ui_admin.kfe11b8"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Live segment preview (debounced)
  useEffect(() => {
    let cancelled = false;
    setPreview((p) => ({ ...p, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          cantons: cantons.join(","),
          covers: covers.join(","),
          target,
        });
        const r = await api.get(`/admin/push/segments-preview?${params.toString()}`);
        if (!cancelled) setPreview({ users: r.data.matching_users, devices: r.data.matching_devices, loading: false });
      } catch {
        if (!cancelled) setPreview({ users: null, devices: null, loading: false });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [cantons, covers, target]);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error(tx("ui_admin.kfde1c3"));
      return;
    }
    if (title.length > 80) { toast.error(tx("r3.title_long")); return; }
    if (body.length > 240) { toast.error(tx("r3.msg_long")); return; }

    // Parse local datetime "YYYY-MM-DDTHH:mm" → ISO UTC (only if in the future)
    let scheduledForIso = null;
    if (scheduledAt) {
      const when = new Date(scheduledAt);
      if (Number.isNaN(when.getTime())) {
        toast.error(tx("r3.bad_schedule")); return;
      }
      if (when.getTime() < Date.now() + 60_000) {
        toast.error(tx("ui_admin.k987d4d")); return;
      }
      scheduledForIso = when.toISOString();
    }

    const confirmMsg = scheduledForIso
      ? `Programmer cette notification pour ${new Date(scheduledForIso).toLocaleString("fr-CH")} (cible « ${target} ») ?`
      : tx("tpl.push_confirm", { target });
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const r = await api.post("/admin/push/broadcast", {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || "/app/accueil",
        target,
        cantons,
        covers,
        scheduled_for: scheduledForIso,
      });
      if (r.data.scheduled) {
        toast.success(tx("tpl.scheduled_for", { date: new Date(r.data.scheduled_for).toLocaleString("fr-CH") }));
      } else {
        toast.success(tx("tpl.push_sent", { users: r.data.targeted_users, devices: r.data.sent }));
      }
      setTitle("");
      setBody("");
      setScheduledAt("");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || tx("ui_admin.kd0df65");
      toast.error(typeof msg === "string" ? msg : tx("ui_admin.kfe7d2c"));
    } finally {
      setSending(false);
    }
  };

  const sendTestToMe = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error(tx("ui_admin.kf20f63"));
      return;
    }
    setTesting(true);
    try {
      const r = await api.post("/admin/push/test-preview", {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || "/app/accueil",
      });
      if (r.data.sent > 0) toast.success(tx("tpl.preview_sent", { n: r.data.sent }));
      else toast.error(tx("ui_admin.k852acd"));
    } catch (e) {
      toast.error(tx("ui_admin.k31d3b1"));
    } finally {
      setTesting(false);
    }
  };

  const cancelScheduled = async (campaignId) => {
    if (!window.confirm(tx("ui_admin.k2caa8b"))) return;
    try {
      await api.post(`/admin/push/campaigns/${campaignId}/cancel`);
      toast.success(tx("ui_admin.ke6829c"));
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || tx("ui_admin.k98f6c7");
      toast.error(typeof msg === "string" ? msg : tx("ui_admin.kfe7d2c"));
    }
  };

  const totalSent = items.reduce((s, c) => s + (c.sent_count || 0), 0);
  const totalClicks = items.reduce((s, c) => s + (c.click_count || 0), 0);
  const avgOpenRate = totalSent > 0 ? Math.round((totalClicks / totalSent) * 1000) / 10 : 0;

  // min datetime = now + 5 minutes, formatted for datetime-local
  const minSchedule = (() => {
    const d = new Date(Date.now() + 5 * 60 * 1000);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60 * 1000).toISOString().slice(0, 16);
  })();

  const targetLabel = {
    all: tx("ui_admin.k612200"),
    active: tx("ui_admin.kfe7b9e"),
    trial: tx("ui_admin.k6befff"),
  };
  const typeBadge = {
    manual: { label: tx("ui_admin.k65314e"), cls: "bg-primary-c/10 text-primary-c" },
    weekend: { label: tx("ui_admin.k20782b"), cls: "bg-secondary-c/10 text-secondary-c" },
    vat: { label: tx("ui_admin.k5177db"), cls: "bg-blue-100 text-blue-700" },
  };
  const statusBadge = {
    scheduled: { label: tx("ui_admin.k758745"), cls: "bg-blue-100 text-blue-700" },
    cancelled: { label: tx("ui_admin.k386f20"), cls: "bg-red-100 text-red-700" },
    pending: { label: "⏳ En cours", cls: "bg-yellow-100 text-yellow-700" },
  };

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={Bell} label={tx("ui_admin.kbbb392")} value={devices} hint={`${subUsers} utilisateur${subUsers > 1 ? "s" : ""}`} />
        <Kpi icon={Send} label={tx("ui_admin.k623070")} value={totalSent} hint="depuis toujours" />
        <Kpi icon={CheckCircle2} label={tx("ui_admin.k6b0574")} value={totalClicks} color="secondary-c" />
        <Kpi icon={TrendingUp} label={tx("ui_admin.k3d90c3")} value={`${avgOpenRate}%`} color="secondary-c" />
      </div>

      {/* Composer */}
      <div className="card-c" data-testid="push-composer">
        <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("r3.new_campaign")}</p>
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-semibold text-text-secondary">{tx("ui_admin.k99fb48")} <span className="text-[10px] text-text-secondary/70">({title.length}/80)</span></label>
            <input
              data-testid="push-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={tx("ui_admin.kf5746b")}
              maxLength={80}
              className="mt-1 w-full input-c"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary">{tx("ui_admin.k4c2a8f")} <span className="text-[10px] text-text-secondary/70">({body.length}/240)</span></label>
            <textarea
              data-testid="push-body-input"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={tx("ui_admin.k381720")}
              maxLength={240}
              className="mt-1 w-full input-c resize-none"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-text-secondary">{tx("ui_admin.kd4cd8e")}</label>
              <input
                data-testid="push-url-input"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/app/accueil"
                className="mt-1 w-full input-c"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">{tx("ui_admin.kef1e68")}</label>
              <select
                data-testid="push-target-select"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1 w-full input-c"
              >
                <option value="all">{tx("ui_admin.k612200")}</option>
                <option value="active">{tx("ui_admin.kfe7b9e")}</option>
                <option value="trial">{tx("ui_admin.k6befff")}</option>
              </select>
            </div>
          </div>

          {/* Segments — cantons */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-text-secondary">
                {tx("tpl.cantons")} <span className="text-[10px] text-text-secondary/70">({cantons.length ? tx("tpl.selected", { n: cantons.length }) : tx("tpl.all_m")})</span>
              </label>
              {cantons.length > 0 && (
                <button data-testid="push-cantons-clear" onClick={() => setCantons([])} className="text-[11px] text-text-secondary hover:text-primary-c">{tx("ui_admin.k1e9a49")}</button>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="push-cantons-chips">
              {CANTONS_LIST.map((c) => {
                const on = cantons.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`push-canton-${c}`}
                    onClick={() => toggle(cantons, c, setCantons)}
                    className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${on ? "bg-primary-c text-white border-primary-c" : "bg-white text-text-secondary border-c hover:border-primary-c/50"}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Segments — covers */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-text-secondary">
                {tx("tpl.size")} <span className="text-[10px] text-text-secondary/70">({covers.length ? tx("tpl.selected", { n: covers.length }) : tx("tpl.all_f")})</span>
              </label>
              {covers.length > 0 && (
                <button data-testid="push-covers-clear" onClick={() => setCovers([])} className="text-[11px] text-text-secondary hover:text-primary-c">{tx("ui_admin.k1e9a49")}</button>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="push-covers-chips">
              {COVERS_BUCKETS.map((c) => {
                const on = covers.includes(c.v);
                return (
                  <button
                    key={c.v}
                    type="button"
                    data-testid={`push-cover-${c.v}`}
                    onClick={() => toggle(covers, c.v, setCovers)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-secondary-c text-white border-secondary-c" : "bg-white text-text-secondary border-c hover:border-secondary-c/50"}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Live preview */}
          {(title || body) && (
            <div className="rounded-xl border border-c bg-surface-muted p-3" data-testid="push-preview">
              <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-2">{tx("ui_admin.k39909e")}</p>
              <div className="rounded-lg bg-white p-3 shadow-sm flex items-start gap-3">
                <div className="h-9 w-9 flex-none rounded-lg bg-[#2A7D5F] text-white flex items-center justify-center font-bold">O</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{title || tx("ui_admin.ka39255")}</p>
                  <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{body || tx("ui_admin.ka90060")}</p>
                  <p className="text-[10px] text-text-secondary mt-1">{tx("r3.now")}</p>
                </div>
              </div>
            </div>
          )}

          {/* Schedule (optional) */}
          <div>
            <label className="text-xs font-semibold text-text-secondary flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              {tx("ui_admin.kcfe2f4")} <span className="text-[10px] text-text-secondary/70">{tx("ui_admin.kee2a81")}</span>
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                data-testid="push-schedule-input"
                type="datetime-local"
                value={scheduledAt}
                min={minSchedule}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="input-c flex-1"
              />
              {scheduledAt && (
                <button
                  type="button"
                  data-testid="push-schedule-clear"
                  onClick={() => setScheduledAt("")}
                  className="text-[11px] text-text-secondary hover:text-primary-c px-2"
                >
                  {tx("ui_admin.k1e9a49")}
                </button>
              )}
            </div>
            {scheduledAt && (
              <p className="mt-1 text-[11px] text-primary-c">
                ⏰ Sera envoyée le {new Date(scheduledAt).toLocaleString("fr-CH")}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
            <p className="text-[11px] text-text-secondary" data-testid="push-audience-count">
              {preview.loading && preview.users === null
                ? "Estimation…"
                : preview.users === null
                ? tx("tpl.will_send", { count: devices, target: targetLabel[target] })
                : (
                  <>
                    {tx("ui_admin.k31bb22")} <strong>{preview.users}</strong> utilisateur{preview.users > 1 ? "s" : ""} · <strong>{preview.devices}</strong> appareil{preview.devices > 1 ? "s" : ""}
                    {(cantons.length > 0 || covers.length > 0) && <span className="text-text-secondary/70"> {tx("ui_admin.k95e059")}</span>}
                  </>
                )}
            </p>
            <div className="flex items-center gap-2">
              <button
                data-testid="push-test-me-btn"
                type="button"
                onClick={sendTestToMe}
                disabled={testing || !title.trim() || !body.trim()}
                title={tx("ui_admin.kbd111f")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-c bg-white px-3 py-2 text-sm font-medium text-text-secondary hover:border-primary-c/50 hover:text-primary-c disabled:opacity-50"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                {tx("ui_admin.k05be6b")}
              </button>
              <button
                data-testid="push-send-btn"
                onClick={send}
                disabled={sending || !title.trim() || !body.trim() || (preview.users === 0)}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {scheduledAt ? "Programmer" : tx("ui_admin.k770a1a")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("r3.history")}</p>
          <button onClick={load} className="p-2 rounded-full hover:bg-surface-muted" data-testid="push-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {loading && <p className="text-sm text-text-secondary text-center py-6">{tx("ui_admin.k8f54e8")}</p>}
        {!loading && items.length === 0 && (
          <div className="card-c text-center py-10">
            <MessageSquare className="w-8 h-8 text-text-secondary mx-auto" strokeWidth={1.5} />
            <p className="text-sm text-text-secondary mt-3">{tx("ui_admin.ke0356c")}</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((c) => {
              const badge = typeBadge[c.type] || typeBadge.manual;
              return (
                <div key={c.id} className="card-c" data-testid={`campaign-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{c.title}</p>
                        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                        {statusBadge[c.status] && (
                          <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${statusBadge[c.status].cls}`}>
                            {statusBadge[c.status].label}
                          </span>
                        )}
                        {(c.cantons?.length > 0) && (
                          <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-primary-c/10 text-primary-c">
                            📍 {c.cantons.join(", ")}
                          </span>
                        )}
                        {(c.covers?.length > 0) && (
                          <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-secondary-c/10 text-secondary-c">
                            👥 {c.covers.join(" · ")}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-secondary mt-1 line-clamp-2">{c.body}</p>
                      <p className="text-[10px] text-text-secondary mt-1">
                        {c.status === "scheduled" && c.scheduled_for
                          ? <>{tx("ui_admin.k780bde")} <strong>{new Date(c.scheduled_for).toLocaleString("fr-CH")}</strong> · </>
                          : `${new Date(c.created_at).toLocaleString("fr-CH")} · `}
                        audience : {targetLabel[c.target] || c.target} · lien : {c.url}
                      </p>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                      {c.status === "scheduled" ? (
                        <button
                          data-testid={`push-cancel-${c.id}`}
                          onClick={() => cancelScheduled(c.id)}
                          className="text-xs px-3 py-1.5 rounded-full font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                        >
                          {tx("ui_admin.kd12c3b")}
                        </button>
                      ) : (
                        <>
                          <p className="text-lg font-display leading-none">{c.open_rate}%</p>
                          <p className="text-[10px] text-text-secondary">{c.click_count} / {c.sent_count} ouvertures</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("stats");

  const isAdmin = user && (user.role === "admin" || user.lifetime_access);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => toast.error(tx("ui_admin.k9d7a40")));
  }, [isAdmin]);
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="card-c max-w-md text-center">
          <p className="font-display text-2xl">{tx("ui_admin.kd646f7")}</p>
          <p className="text-sm text-text-secondary mt-2">{tx("ui_admin.kbeea58")}</p>
          <Link to="/app/accueil" className="btn-primary inline-block mt-6">{tx("ui_admin.kf27714")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg pb-16">
      <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-4 flex items-center justify-between">
        <Link to="/app/accueil" className="text-sm text-text-secondary hover:text-[var(--text)] flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> {tx("ui_admin.k7727eb")}
        </Link>
        <span className="text-[10px] font-bold uppercase tracking-wider text-primary-c bg-primary-c/10 px-2 py-1 rounded-full">{tx("ui_admin.k0e6c55")}</span>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 page-enter">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-3xl sm:text-4xl">{tx("ui_admin.k3a1fa9")}</h1>
          <p className="text-text-secondary text-sm mt-1">{tx("ui_admin.k212214")}</p>
        </motion.div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap items-center gap-1 p-1 bg-surface-muted rounded-2xl border border-c max-w-full" data-testid="admin-tabs">
          {[
            { k: "stats", label: "📊 Stats", icon: TrendingUp },
            { k: "leads", label: "📞 Leads", icon: Phone },
            { k: "users", label: tx("r3.users"), icon: Users },
            { k: "push", label: "📣 Push", icon: Bell },
            { k: "cancellations", label: t("admin.tab_cancellations") },
            { k: "settings", label: t("admin.tab_settings") },
          ].map((it) => (
            <button
              key={it.k}
              onClick={() => setTab(it.k)}
              data-testid={`admin-tab-${it.k}`}
              className={`px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-all ${tab === it.k ? "bg-white shadow text-[var(--text)]" : "text-text-secondary"}`}
            >
              {it.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "stats" && stats && (
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-3">👥 Utilisateurs</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Kpi icon={Users} label={tx("ui_admin.k96b014")} value={stats.users.total} hint={`+${stats.users.last_7d} cette semaine`} />
                  <Kpi icon={Sparkles} label={tx("ui_admin.k20e505")} value={stats.users.trials_active} />
                  <Kpi icon={CheckCircle2} label={tx("ui_admin.k212a95")} value={stats.users.active_subs} hint={`${stats.users.monthly_subs}m · ${stats.users.yearly_subs}a`} />
                  <Kpi icon={Award} label={tx("ui_admin.kc5340e")} value={stats.users.ambassadors} hint="Code AMBASSADEUR2026" />
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-3">{tx("r3.revenue")}</p>
                <div className="grid grid-cols-2 gap-3">
                  <Kpi icon={Wallet} label="MRR" value={`${stats.revenue.mrr_chf.toLocaleString("fr-CH")} CHF`} hint={tx("ui_admin.k686b14")} color="primary-c" />
                  <Kpi icon={TrendingUp} label="ARR" value={`${stats.revenue.arr_chf.toLocaleString("fr-CH")} CHF`} hint={tx("ui_admin.k07b28a")} color="secondary-c" />
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-3">{tx("ui_admin.k98da22")}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Kpi icon={Phone} label={tx("ui_admin.k471a54")} value={stats.leads.total} hint={tx("tpl.to_call", { n: stats.leads.open })} />
                  <Kpi icon={Calendar} label={tx("ui_admin.kdb209c")} value={stats.leads.last_7d} />
                  <Kpi icon={Wallet} label={tx("ui_admin.k230c43")} value={stats.activity.total_sales} />
                  <Kpi icon={Users} label={tx("ui_admin.kf510a8")} value={stats.activity.total_invoices} />
                </div>
              </div>

              <div className="card-c bg-gradient-to-br from-[#FFF8EB] to-white border-secondary-c/30">
                <div className="flex items-center gap-3">
                  <Gift className="w-6 h-6 text-secondary-c" />
                  <p className="font-display text-lg">Ambassadeurs — Code {stats.ambassador.code}</p>
                </div>
                <p className="text-sm text-text-secondary mt-2">
                  <strong>{stats.ambassador.used_real}</strong> {tx("ui_admin.kff1041")} <strong>{stats.ambassador.total - stats.ambassador.used_seed}</strong> places disponibles ({stats.ambassador.remaining_public} restantes affichées publiquement).
                </p>
                <p className="text-xs text-text-secondary mt-1">Seed offset marketing : {stats.ambassador.used_seed} (pour créer l'urgence sur la landing).</p>
              </div>
            </div>
          )}

          {tab === "leads" && <LeadsTab />}
          {tab === "users" && <UsersTab />}
          {tab === "push" && <PushTab />}
          {tab === "cancellations" && <AdminCancellations />}
          {tab === "settings" && <AdminSettings />}
        </div>
      </main>
    </div>
  );
}
